import { useState, useEffect } from 'react';
import { teamService, userService, hotelService } from '../../services/api';

const TeamList = () => {
  const [teams, setTeams] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isLead, setIsLead] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    email: '',
    hotelId: '',
    is_active: true,
  });

  useEffect(() => {
    fetchHotels();
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [selectedHotel]);

  const fetchHotels = async () => {
    try {
      const response = await hotelService.getAll();
      setHotels(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch hotels:', error);
    }
  };

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const params = selectedHotel ? { hotelId: selectedHotel } : {};
      const response = await teamService.getAll(params);
      setTeams(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (team) => {
    setSelectedTeam(team);
    setFormData({
      name: team.name || '',
      code: team.code || '',
      description: team.description || '',
      email: team.email || '',
      hotelId: team.hotel_id || '',
      is_active: team.is_active !== false,
    });
    setShowEditModal(true);
  };

  const handleViewMembers = async (team) => {
    setSelectedTeam(team);
    setShowMembersModal(true);
    await fetchTeamMembers(team.id);
  };

  const fetchTeamMembers = async (teamId) => {
    setMembersLoading(true);
    try {
      const response = await teamService.getMembers(teamId);
      setTeamMembers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      setTeamMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleAddTeam = () => {
    setSelectedTeam(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      email: '',
      hotelId: selectedHotel || (hotels.length > 0 ? hotels[0].id : ''),
      is_active: true,
    });
    setShowAddModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedTeam) {
        await teamService.update(selectedTeam.id, formData);
      } else {
        await teamService.create(formData);
      }
      setShowEditModal(false);
      setShowAddModal(false);
      fetchTeams();
    } catch (error) {
      console.error('Failed to save team:', error);
      alert(error.response?.data?.message || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleOpenAddMember = async () => {
    setShowAddMemberModal(true);
    setSelectedUserId('');
    setIsLead(false);
    setUsersLoading(true);
    try {
      const response = await userService.getAll();
      const memberIds = teamMembers.map(m => m.id);
      const availableUsers = (response.data.data || []).filter(
        user => !memberIds.includes(user.id)
      );
      setAllUsers(availableUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAllUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    setAddingMember(true);
    try {
      await userService.assignTeam(selectedUserId, selectedTeam.id, isLead);
      setShowAddMemberModal(false);
      await fetchTeamMembers(selectedTeam.id);
    } catch (error) {
      console.error('Failed to add member:', error);
      alert(error.response?.data?.message || 'Failed to add member to team');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    setRemovingMemberId(userId);
    try {
      await userService.removeTeam(userId, selectedTeam.id);
      await fetchTeamMembers(selectedTeam.id);
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert(error.response?.data?.message || 'Failed to remove member from team');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowMembersModal(false);
    setShowAddModal(false);
    setShowAddMemberModal(false);
    setSelectedTeam(null);
    setTeamMembers([]);
    setAllUsers([]);
  };

  const closeAddMemberModal = () => {
    setShowAddMemberModal(false);
    setSelectedUserId('');
    setIsLead(false);
  };

  // Group teams by hotel
  const teamsByHotel = teams.reduce((acc, team) => {
    const hotelName = team.hotel_name || 'Unassigned';
    if (!acc[hotelName]) {
      acc[hotelName] = [];
    }
    acc[hotelName].push(team);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-gray-600">Manage support teams by hotel</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedHotel}
            onChange={(e) => setSelectedHotel(e.target.value)}
            className="select"
          >
            <option value="">All Hotels</option>
            {hotels.map((hotel) => (
              <option key={hotel.id} value={hotel.id}>
                {hotel.name} ({hotel.code})
              </option>
            ))}
          </select>
          <button onClick={handleAddTeam} className="btn-primary">
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Team
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner h-8 w-8"></div>
        </div>
      ) : teams.length > 0 ? (
        <div className="space-y-8">
          {Object.entries(teamsByHotel).map(([hotelName, hotelTeams]) => (
            <div key={hotelName}>
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
                  <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{hotelName}</h2>
                <span className="ml-2 badge badge-gray">{hotelTeams.length} teams</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hotelTeams.map((team) => (
                  <div key={team.id} className="card hover:shadow-lg transition-shadow">
                    <div className="card-body">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{team.name}</h3>
                          <span className="badge badge-purple mt-1">{team.code}</span>
                        </div>
                        <span className={`badge ${team.is_active ? 'badge-green' : 'badge-red'}`}>
                          {team.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {team.description && (
                        <p className="mt-3 text-sm text-gray-600 line-clamp-2">{team.description}</p>
                      )}

                      {team.email && (
                        <div className="mt-3 flex items-center text-sm text-gray-600">
                          <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="truncate">{team.email}</span>
                        </div>
                      )}

                      <div className="mt-4 pt-4 border-t flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(team)}
                          className="btn-secondary btn-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleViewMembers(team)}
                          className="btn-secondary btn-sm"
                        >
                          Members
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <svg className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p>No teams found</p>
          {selectedHotel && (
            <p className="text-sm mt-1">Try selecting a different hotel or clear the filter</p>
          )}
        </div>
      )}

      {/* Edit/Add Team Modal */}
      {(showEditModal || showAddModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModals}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedTeam ? 'Edit Team' : 'Add New Team'}
                </h3>
              </div>

              <form onSubmit={handleSave}>
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="label">Hotel</label>
                    <select
                      name="hotelId"
                      value={formData.hotelId}
                      onChange={handleInputChange}
                      className="select"
                      required
                    >
                      <option value="">Select a hotel...</option>
                      {hotels.map((hotel) => (
                        <option key={hotel.id} value={hotel.id}>
                          {hotel.name} ({hotel.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Team Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g., IT Support"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Code</label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g., IT"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="textarea"
                      rows={3}
                      placeholder="Brief description of the team..."
                    />
                  </div>

                  <div>
                    <label className="label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="team@hotel101global.com"
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  <button type="button" onClick={closeModals} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="btn-primary">
                    {saving ? (
                      <>
                        <div className="spinner spinner-sm mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Team'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && selectedTeam && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModals}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedTeam.name}</h3>
                  <p className="text-sm text-gray-500">{selectedTeam.hotel_name} - Team Members</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleOpenAddMember}
                    className="btn-primary btn-sm"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Member
                  </button>
                  <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
                {membersLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="spinner h-8 w-8"></div>
                  </div>
                ) : teamMembers.length > 0 ? (
                  <div className="space-y-3">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.first_name} {member.last_name}
                            </p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {member.is_lead && (
                            <span className="badge badge-primary">Team Lead</span>
                          )}
                          <span className="badge badge-gray">{member.role_name || 'Member'}</span>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            disabled={removingMemberId === member.id}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove from team"
                          >
                            {removingMemberId === member.id ? (
                              <div className="spinner spinner-sm"></div>
                            ) : (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p>No members in this team</p>
                    <p className="text-sm mt-1">Click "Add Member" to add users to this team</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}
                </span>
                <button onClick={closeModals} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedTeam && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeAddMemberModal}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Add Member to {selectedTeam.name}</h3>
              </div>

              <form onSubmit={handleAddMember}>
                <div className="px-6 py-4 space-y-4">
                  {usersLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="spinner h-8 w-8"></div>
                    </div>
                  ) : allUsers.length > 0 ? (
                    <>
                      <div>
                        <label className="label">Select User</label>
                        <select
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          className="select"
                          required
                        >
                          <option value="">Choose a user...</option>
                          {allUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={isLead}
                            onChange={(e) => setIsLead(e.target.checked)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">Assign as Team Lead</span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No available users to add</p>
                      <p className="text-sm mt-1">All users are already members of this team</p>
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  <button type="button" onClick={closeAddMemberModal} className="btn-secondary">
                    Cancel
                  </button>
                  {allUsers.length > 0 && (
                    <button type="submit" disabled={addingMember || !selectedUserId} className="btn-primary">
                      {addingMember ? (
                        <>
                          <div className="spinner spinner-sm mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        'Add Member'
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamList;
